import { FaListUl, FaHandHoldingUsd, FaExchangeAlt, FaUserShield, FaEye } from "react-icons/fa";
import { Tooltip } from 'react-tooltip';


// Utilitaires
import {formatPhoneNumber, getAccountDisplayName } from "../service/utils/accountUtils";
import ImageWithApi from "./ImageWithSas";
import ValidationStatusBadge from '../ValidationStatusBadge';
import AccountActionsDropdown from "./AccountActionsDropdown";
import { VALIDATION_STATUS } from "../service/utils/validationStatus";
import { formatAmountInThousands } from "../service/utils/formatters";

/**
 * LIGNE DE COMPTE - Affiche les informations d'un compte dans le tableau
 * @param {Object} props
 * @param {Object} props.account - Données du compte
 * @param {Object} props.selectedDomain - Domaine sélectionné
 * @param {Function} props.handleOpenSignatoriesModal - Callback ouvrir modal signataires
 * @param {Function} props.handleOpenDetailsModal - Callback ouvrir détails
 * @param {Function} props.handleOpenValidationModal - Callback ouvrir validation
 * @param {boolean} props.isCurrentUserValidator - Si utilisateur est validateur
 * @param {Function} props.handleOpenModalOrders - Callback ouvrir commandes
 * @param {Function} props.handleOpenCashEditModal - Callback éditer cash
 * @param {Function} props.handleOpenModalSecurities - Callback ouvrir titres
 * @param {Function} props.handleOpenModalTransactions - Callback ouvrir transactions
 * @param {Function} props.handleShowEditAccountModal - Callback éditer compte
 * @param {Function} props.handleDeleteSuccess - Callback succès suppression
 * @param {Function} props.handleGenerateTitleReport - Callback générer rapport titres
 * @param {Function} props.handleGenerateTransactionReport - Callback générer rapport transactions
 * @param {Function} props.openContactForm - Callback ouvrir formulaire contact
 * @param {Function} props.openContactList - Callback ouvrir liste contacts
 * @param {Function} props.openModal - Callback ouvrir modal
 * @param {Function} props.handleOpenModal - Callback ouvrir modal représentants
 * @param {Function} props.openAssociationForm - Callback ouvrir formulaire association
 * @param {Function} props.openAssociationList - Callback ouvrir liste association
 * @param {Function} props.handleOpenApprovalDetailsModal - Callback ouvrir détails approbation
 * @param {Function} props.handleOpenSubmitAccountConfirmation - Callback soumettre validation
 * @param {Function} props.handleOpenValidationDetails - Callback ouvrir détails validation
 */

const AccountRow = ({ 
  account, 
  selectedDomain,
  handleOpenSignatoriesModal, 
  handleOpenDetailsModal, 
  handleOpenValidationModal, 
  isCurrentUserValidator, 
  handleOpenModalOrders, 
  handleOpenCashEditModal, 
  handleOpenModalSecurities, 
  handleOpenModalTransactions, 
  handleShowEditAccountModal, 
  handleDeleteSuccess, 
  handleGenerateTitleReport, 
  handleGenerateTransactionReport, 
  openContactForm, 
  openContactList, 
  openModal, 
  handleOpenModal, 
  openAssociationForm, 
  openAssociationList, 
  handleOpenApprovalDetailsModal, 
  handleOpenSubmitAccountConfirmation,
  handleOpenValidationDetails,
  handleOpenManagedAccounts ,
  handleOpenHistoricsDocuments,
}) => {
  
  // const accountDisplayName = getAccountDisplayName(account);
  // Utiliser displayName si disponible, sinon fallback sur l'ancienne méthode
  const accountDisplayName = account.displayName || getAccountDisplayName(account);
  
  // Utiliser accountTypeDisplay si disponible, sinon accountTypeName
  const accountTypeDisplay = account.accountTypeDisplay || account.accountTypeName;


  return (
    <tr key={account.id}>
      {/* Colonne Nom */}
      <td>
        <div className="account-name">
          <ImageWithApi
            accountId={account.id}
            accountType={account.accountTypeName}
            email={account.email}
            alt={accountDisplayName}
            className="account-avatar"
            imageType="auto"
            fallbackToGravatar={true}
            style={{ width: '50px', height: '50px' }}
          />
            <span className="text text-uppercase"
            >
              {accountDisplayName}
            </span>
        </div>
      </td>

      <td>
        {account.email ? (
          <a
            href={`mailto:${account.email}`}
            className="text-decoration-none"
            title="Envoyer un email"
          >
            {account.email}
          </a>
        ) : (
          <span className="text-muted fst-italic" title="Type de compte">
            {accountTypeDisplay}
          </span>
        )}
      </td>

      {/* NOUVELLE COLONNE - Compte Administré */}
      <td className="text text-left">
        {account.isManagedAccount ? (
          <span 
            className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25"
            data-tooltip-id={`managed-indicator-${account.id}`}
            data-tooltip-content="Ce compte est administré par un représentant légal"
          >
            <FaUserShield className="me-2" size={10} />
            Oui
          </span>
        ) : (
          <span className="badge rounded-pill bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25">Non</span>
        )}
        <Tooltip id={`managed-indicator-${account.id}`} />
      </td>

      {/* Colonne Téléphone */}
      <td>{formatPhoneNumber(account.phoneNumber)}</td>
      
      {/* Colonne AUC */}
      <td className="text-end">
        <span className="currency-value">
          {formatAmountInThousands(account.cashAUC || 0)} 
        </span>
      </td>
      
      {/* Colonne Cash */}
      <td className="text-end">
        <span className="currency-value">
          {formatAmountInThousands(account.cash || 0)}
        </span>
      </td>
      
      {/* Colonne Type */}
      <td className="text-center">
        <span className="badge bg-light rounded-5 text-dark border border-opacity-25 bg-opacity-10">
          {account.accountTypeName === "Particular"
            ? "Particulier"
            : account.accountTypeName === "Issuer"
            ? "Émetteur"
            : account.accountTypeName}
        </span>
      </td>
      
      {/* Colonne Validation */}
      <td className="py-2 text-center">
        <a
          href="#"
          onClick={(e) => {
            if (account.validationStatus === VALIDATION_STATUS.DRAFT) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleOpenValidationDetails(account.id, 'Account');
          }}
          data-tooltip-id={`validation-details-tooltip-${account.id}`}
          data-tooltip-content={
            account.validationStatus === VALIDATION_STATUS.DRAFT
              ? "Statut en brouillon — détails indisponibles"
              : "Voir les détails de validation"
          }
          style={{
            pointerEvents: account.validationStatus === VALIDATION_STATUS.DRAFT ? "none" : "auto",
          }}
        >
          <ValidationStatusBadge status={account.validationStatus} />
        </a>
        <Tooltip id={`validation-details-tooltip-${account.id}`} />
      </td>
      
      {/* Colonne Actions */}
      <td className="actions-cell">
        <div className="action-buttons">
          {/* Bouton Voir plus */}
          <div 
            className="action-icon" 
            data-tooltip-id="tooltip-voirplus" 
            data-tooltip-content={`Voir les détails du compte de ${accountDisplayName}`} 
            onClick={() => handleOpenDetailsModal(account)}
          >
            <FaEye size={14} />
          </div>
          <Tooltip id="tooltip-voirplus" place="top" />
          
          {/* Bouton Commandes */}
          <div 
            className="action-icon" 
            data-tooltip-id="tooltip-ordres" 
            data-tooltip-content={`Voir les ordres du client ${accountDisplayName}`} 
            onClick={() => handleOpenModalOrders(account.id)}
          >
            <FaListUl size={14} />
          </div>
          <Tooltip id="tooltip-ordres" place="top" />
          
          {/* Bouton Éditer Cash */}
          <div 
            className="action-icon" 
            onClick={() => handleOpenCashEditModal(account)} 
            data-tooltip-id="tooltip-edit-cash" 
            data-tooltip-content={`Ajouter du cash au client ${accountDisplayName}`}
          >
            <FaHandHoldingUsd size={14} />
          </div>
          <Tooltip id="tooltip-edit-cash" place="top" />
          
          {/* Bouton Titres */}
          <div 
            className="action-icon" 
            data-tooltip-id="tooltip-titres" 
            data-tooltip-content={`Voir les titres du client ${accountDisplayName}`} 
            onClick={() => handleOpenModalSecurities(account.id)}
          >
            <FaListUl size={14} />
          </div>
          <Tooltip id="tooltip-titres" place="top" />
          
          {/* Bouton Transactions */}
          <div 
            className="action-icon" 
            data-tooltip-id="tooltip-transactions" 
            data-tooltip-content={`Afficher les transactions du client ${accountDisplayName}`} 
            onClick={() => handleOpenModalTransactions(account.id)}
          >
            <FaExchangeAlt size={14} />
          </div>
          <Tooltip id="tooltip-transactions" place="top" />
          
          {/* Menu déroulant des actions */}
          <AccountActionsDropdown
            account={account}
            selectedDomain={selectedDomain}
            handleOpenSignatoriesModal={handleOpenSignatoriesModal}
            handleOpenDetailsModal={handleOpenDetailsModal}
            handleOpenValidationModal={handleOpenValidationModal}
            isCurrentUserValidator={isCurrentUserValidator}
            handleOpenModalOrders={handleOpenModalOrders}
            handleOpenCashEditModal={handleOpenCashEditModal}
            handleOpenModalSecurities={handleOpenModalSecurities}
            handleOpenModalTransactions={handleOpenModalTransactions}
            handleShowEditAccountModal={handleShowEditAccountModal}
            handleDeleteSuccess={handleDeleteSuccess}
            handleGenerateTitleReport={handleGenerateTitleReport}
            handleGenerateTransactionReport={handleGenerateTransactionReport}
            openContactForm={openContactForm}
            openContactList={openContactList}
            openModal={openModal}
            handleOpenModal={handleOpenModal}
            openAssociationForm={openAssociationForm}
            openAssociationList={openAssociationList}
            handleOpenApprovalDetailsModal={handleOpenApprovalDetailsModal}
            handleOpenSubmitAccountConfirmation={handleOpenSubmitAccountConfirmation}
            handleOpenManagedAccounts={handleOpenManagedAccounts} 
            handleOpenHistoricsDocuments={handleOpenHistoricsDocuments}
          />
        </div>
      </td>
    </tr>
  );
};

export default AccountRow;